/* Verbatim port of renderExec from service/review/index.html (~1390-1567) —
 * §0 Executive summary.
 *
 * PARAMETERIZED (rule a): reads the global `STATE` throughout — takes
 * `state: AppPayloadState` as its one explicit parameter. `ACT_STATUS` is
 * sourced from `actStatusStore.get()` internally (not a second explicit
 * param) so every entry in views/registry.ts's renderer map — this real
 * renderer and the eight still-stubbed ones — shares the same
 * `(state: AppPayloadState) => void` shape.
 *
 * GUARD (documented per the F2a brief): every `typeof WS !== 'undefined'
 * && WS.available === true` becomes `wsAvailable()` (hub/src/state/ui.ts),
 * which returns false until F2d ports the real AM workspace layer. The
 * WS-branch bodies below (the shared-board gap-chip status buttons, the
 * live top-five-moves list, the data-wsact click wiring) are kept
 * byte-identical to the original — they are part of the same innerHTML
 * template — but are unreachable while wsAvailable() is false, so the exec
 * section renders in its "workspace unavailable" (derived, read-only)
 * form. They type-check against the real, typed `WS` module (state/
 * workspace.ts's `declare global` block) — see that file for why the bare
 * identifier resolves without an import.
 *
 * F3 (wave B): innerHTML → lit render(). `tip()` calls → `tipT()` (lib/
 * templates.ts); `chip()` this file never used. `CONTROL_TIER_LINE` (lib/
 * glossary.ts) is a static raw-HTML-string constant — rather than
 * unsafeHTML it, a local TemplateResult twin (`CONTROL_TIER_LINE_T`,
 * verbatim markup) is built right here, since it has exactly one consumer
 * in the whole codebase (grepped) — same "local, non-shared helper stays
 * local" treatment F3a gave conflicts.ts's chipFor/trackedIn, just for a
 * constant instead of a function.
 *
 * SELF-CORRECTION (planeVerdicts' `.num` field, derive/entity.ts): two of
 * the three verdict cards format `.num` as a string containing a literal
 * `<small>...</small>` suffix (e.g. `"82<small>/98</small>"` — see
 * ui/exports.ts's own `strip(v.num)` call, which confirms the field is
 * knowingly HTML-bearing). Rather than unsafeHTML it (or reach into
 * derive/entity.ts, out of this task's scope, to split the field in two),
 * `numT()` below parses the ONE fixed `<small>` pattern the field ever
 * takes and rebuilds it as a native TemplateResult with `<small>` as
 * static markup — the same "avoid unsafeHTML when the value can be
 * reassembled as a real template" call F3a made for entity.ts's kpWord/
 * chainClause. The one verdict shape with no `<small>` (entity plane) is
 * returned as plain text unchanged.
 *
 * The gapchip's own open/close toggle (`g.classList.toggle('open')`,
 * originally a delegated querySelectorAll('.gapchip') handler) and the
 * data-wsact status-button click (`ev.stopPropagation(); ...
 * WS.statusMenu(b, t)`, originally a delegated querySelectorAll(
 * '[data-wsact]') handler covering BOTH the gapchip buttons and the
 * top-five-moves buttons) both move to `@click` at their own element —
 * `wsactClick()` is the one shared closure for the latter, called from
 * all three data-wsact sites in this file's template, re-resolving the
 * task from `WS.tasks` by id at click time (not the render-time closure),
 * matching conflicts.ts's chipFor precedent exactly.
 *
 * F4 item 3 (store decoupling): subscribes to wsChangeStore (state/
 * wsChange.ts) below so a WS data change (a task PATCH landing, WS.init()
 * finishing its probe, etc.) re-renders this section — replacing the old
 * hardcoded `renderExec(state)` call state/workspace.ts's
 * `repaintAll()`/`init()` used to make directly.
 */
import { html, nothing, render, type TemplateResult } from 'lit';
import { CAT_COLOR, CAT_LABEL, BAD_STATUS, canEdit } from '../derive/axes.ts';
import { actionPlan } from '../derive/actions.ts';
import { aiHeadline } from '../derive/ai.ts';
import { coveragePanel, type CoverageLayer } from '../derive/coverage.ts';
import { entityHeadline, planeVerdicts } from '../derive/entity.ts';
import { coreAsset, footprint } from '../derive/exec.ts';
import { peerRank, searchHeadline } from '../derive/search.ts';
import { cap, domainOf, factSrc, factVal, faviconUrl, listJoin, pct } from '../lib/format.ts';
import { tipT } from '../lib/templates.ts';
import { actStatusStore, wsAvailable } from '../state/ui.ts';
import { payloadStore } from '../state/payload.ts';
import { wsChangeStore } from '../state/wsChange.ts';
import { openDrawer } from '../ui/drawer.ts';
import { buildTaskHref } from '../ws/taskLink.ts';
import { chartCaption, chartTableTwin, stackedBarLayout, stackedBarSvg } from '../ui/charts.ts';
import { chartExportBar } from '../ui/chartExport.ts';
import type { AppPayloadState } from '../derive/state-shape.ts';
import type { NormalizedAsset } from '../types/contract.ts';

// Verbatim logic mirror of lib/glossary.ts's CONTROL_TIER_LINE (only
// consumer in the codebase — grepped) — see this file's doc comment.
const CONTROL_TIER_LINE_T = html`<b>Owned</b> = client properties · <b>Editable</b> = profiles the client runs on other platforms · <b>Claimable</b> = could be claimed, hasn't been · <b>Not controlled</b> = press, wikis, forums`;

// planeVerdicts' `.num` is either a bare number-as-string (entity plane) or
// `"<n><small>...</small>"` (search/AI planes) — see this file's doc
// comment for why this is parsed here instead of unsafeHTML'd.
function numT(num: string): TemplateResult | string {
  const m = /^(.*?)<small>(.*?)<\/small>$/.exec(num);
  return m ? html`${m[1]}<small>${m[2]}</small>` : num;
}

// C1 coverage panel: per-layer count line. Mapping layers show their parsed
// search/fetch counts; the two enrichment-reconciled layers (search→IMPACT,
// AI→AIQ) show the plane + what it measured. A covered layer with no counts
// reads "completed"; an uncovered one reads "not run".
function covCountText(l: CoverageLayer): string {
  const parts: string[] = [];
  if (l.searches != null) parts.push(`${l.searches} searches`);
  if (l.fetches != null) parts.push(`${l.fetches} fetches`);
  if (parts.length) return parts.join(' · ');
  return l.checked ? 'completed' : 'not run';
}

// shared by the gapchip status buttons AND the top-five-moves list buttons
// (both render a data-wsact button) — re-resolves the task from WS.tasks by
// id at click time, same as conflicts.ts's chipFor.
function wsactClick(ev: MouseEvent, id: number): void {
  ev.stopPropagation();
  const t = WS.tasks.find((x) => x.id === id);
  if (t) WS.statusMenu(ev.currentTarget as HTMLElement, t);
}

export function renderExec(state: AppPayloadState): void {
  const el = document.getElementById('execBody')!;
  const fp = footprint(state);
  const sh = searchHeadline(state);
  const ah = aiHeadline(state);
  const eh = entityHeadline(state);
  const pr = peerRank(state);
  const verdicts = planeVerdicts(state);
  const acts = actionPlan(state, actStatusStore.get());
  const core = coreAsset(state);
  const ent = state.entity;
  const cov = coveragePanel(state);
  const synthesis = state.synthesis;
  // Scope-aware chip grammar: a profile-scoped run reports the IN-SCOPE
  // denominator and how many layers were deliberately scoped out — never
  // folding a skipped layer into "thin" (which would contradict the §0 scope
  // line). thin is still surfaced when in-scope layers are genuinely thin.
  const covChip = cov
    ? (cov.scopedCount
        ? `${cov.checkedCount}/${cov.inScopeTotal} in-scope layers checked`
        : `${cov.checkedCount}/${cov.total} layers checked`) +
      (cov.thinCount ? ` · ${cov.thinCount} thin` : '') +
      (cov.scopedCount ? ` · ${cov.scopedCount} scoped out` : '')
    : '';

  /* ── the brand-presence hub band: who the client is online, at a glance ── */
  const facts = (ent && ent.facts) || {};
  const factRows = Object.entries(facts).slice(0, 9).map(([k, v]) => {
    const src = factSrc(v);
    return html`<div class="f"><dt>${k.replace(/([a-z])([A-Z])/g,'$1 $2')}</dt><dd>${factVal(v)}${src ? html` <a href="${src}" target="_blank" rel="noopener" title="source: ${domainOf(src)||''}" style="color:var(--azure);text-decoration:none;font-size:10px">↗</a>` : nothing}</dd></div>`;
  });

  // official channels: the core site + the flagship account per platform
  const isSoc = (a: NormalizedAsset) => a.assetType === 'social-profile' || a.assetType === 'video-channel';
  const chan = state.assets.filter((a) => (a.category === 'owned' || canEdit(a)) && a.scope !== 'executive'
      && (a.isCoreEntity || isSoc(a)));
  const groups: Record<string, NormalizedAsset[]> = {};
  chan.filter(isSoc).forEach((a) => (groups[a.platform || 'other'] = groups[a.platform || 'other'] || []).push(a));
  const chanCards = [
    ...(core ? [core] : []),
    ...Object.values(groups).map((items) => {
      // String(...) inside parseInt is the exact coercion the untyped code relied on
      items.sort((a,b) => (Number(b.scope === 'global') - Number(a.scope === 'global'))
        || (parseInt(String(b.fields && b.fields.followers)) || 0) - (parseInt(String(a.fields && a.fields.followers)) || 0));
      return items[0];
    })
  ].filter((a): a is NormalizedAsset => !!a).map((a) => {
    // String(a.platform) reproduces the raw JS index coercion (null -> "null")
    const rest = a.isCoreEntity ? 0 : (groups[String(a.platform)] || []).length - 1;
    const dot = a.sentiment === 'negative' ? 'var(--neg)' : BAD_STATUS.includes(a.status) ? 'var(--amber)' : 'var(--green-tx)';
    const foll = a.fields && (a.fields.followers || a.fields.rating);
    return html`<div class="chancard" data-id="${a.id}" @click=${() => openDrawer(state, a.id)}>
      <img src="${faviconUrl(a.url, 64)}" alt="">
      <div class="cm"><div class="p">${a.isCoreEntity ? (domainOf(a.url) || a.title) : (a.platform || a.title)}</div>
        <div class="s">${a.status}${foll ? ' · ' + String(foll) : ''}${rest > 0 ? ` · +${rest} regional` : ''}</div></div>
      <span class="chanstatus" style="background:${dot}" title="${a.status}"></span></div>`;
  });

  // noticeably missing / unclaimed: creation gaps + unclaimed high-value listings
  // `claim` doubles as the fallback for unknown verbs — hoisted so the
  // fallback read is non-optional under noUncheckedIndexedAccess.
  const CLAIM_MOVE = (p: string) => `Go through ${p}'s owner-verification flow to claim the listing, then complete the brand fields (name, address, links, imagery) so it reads as official.`;
  const GAP_MOVE: Record<string, (p: string) => string> = {
    create: p => `Create and brand an official ${p} presence, then publish an initial content pass so the profile outranks lookalikes and stale mirrors.`,
    claim: CLAIM_MOVE,
    consolidate: p => `Audit the duplicate or regional ${p} presences, merge or officially interlink them, and standardize naming so searchers and engines see one authoritative account.`,
    reactivate: p => `Bring the dormant ${p} presence back to life — fresh content, corrected facts, and a link from the main site so engines re-trust it.`,
    fix: p => `Correct the facts on ${p} at the source and add references — fixes propagate from there to search and AI answers.`,
  };
  const wsLive = wsAvailable();
  const gaps = (state.opportunities || []).map((o, i) => {
    // "reactivate" is wrong for a live Wikidata item — normalize the verb
    const verb = /wikidata/i.test(o.platform || '') && o.action === 'reactivate' ? 'fix' : o.action;
    const peers = (o.peersPresent || []).filter(Boolean);
    // working state on the gap itself: an implemented-pending gap must not
    // read identical to an untouched one on the Monday-scan surface
    const gt = wsLive ? WS.tasks.find((x) => x.action_key.startsWith('opp:') && x.detail === o.rationale) : null;
    return html`<div class="gapchip prio-${o.priority||'low'}" data-gx="${i}" @click=${(ev: MouseEvent) => (ev.currentTarget as HTMLElement).classList.toggle('open')}>
      <div class="gx-head"><b>${verb}</b> ${o.platform}${gt && gt.status !== 'open' ? html`<button class="wsst s-${gt.status} pill-sm" data-wsact="${gt.id}" style="margin-left:6px" title="${WS.STATUS_FULL[gt.status]||gt.status} — click to change" @click=${(ev: MouseEvent) => wsactClick(ev, gt.id)}>${WS.STATUS_LABEL[gt.status]||gt.status}</button>` : nothing}<span class="gx-caret">▸</span></div>
      <div class="gx-body">
        <div class="gx-why">${o.rationale || ''}</div>
        ${peers.length ? html`<div class="gx-peers">Peers already there: ${peers.join(', ')}</div>` : nothing}
        <div class="gx-act"><b>Action</b> ${(GAP_MOVE[verb] || CLAIM_MOVE)(o.platform || 'the platform')}</div>
        ${gt && gt.status === 'open' ? html`<div style="margin-top:6px"><button class="wsst s-open pill-sm" data-wsact="${gt.id}" @click=${(ev: MouseEvent) => wsactClick(ev, gt.id)}>Open — click to start</button></div>` : nothing}
      </div></div>`; });

  // CH-3 ownership mix (chart-pack §1): horizontal stacked bar of the footprint,
  // fixed order owned → editable → claimable → not-controlled. The BAR drops
  // zero-count segments; the legend lists ALL FOUR with counts (never a hidden
  // category, §CH-3). Same numbers ledgerRibbon showed, upgraded to §2 marks.
  const ch3all = [
    { key: 'owned', label: CAT_LABEL.owned, value: fp.owned },
    { key: 'editable', label: CAT_LABEL.editable, value: fp.editable },
    { key: 'claimable', label: CAT_LABEL.claimable, value: fp.claimable },
    { key: 'media', label: CAT_LABEL.media, value: fp.media },
  ];
  const ch3 = stackedBarLayout(ch3all);
  const ch3aria = ch3.total
    ? `Ownership mix of ${ch3.total} mapped surfaces: ${ch3all.filter((s) => s.value).map((s) => `${s.value} ${s.label.toLowerCase()}`).join(', ')}.`
    : 'Ownership mix: no mapped surfaces yet.';
  const ledgerLegend = ch3all.map((s) => html`<span><i style="background:${CAT_COLOR[s.key]}"></i>${tipT(s.label, s.label)} <em>${s.value}</em></span>`);
  const ch3cap = `Share of ${ch3.total} mapped ${ch3.total === 1 ? 'surface' : 'surfaces'} the client controls · this run`;
  const ch3cols = ['Category', 'Count', 'Share'];
  const ch3rows = ch3all.map((s) => [s.label, s.value, ch3.total ? `${Math.round((s.value / ch3.total) * 100)}%` : '0%']);

  /* ── the findings paragraph: plain sentences, real numbers ── */
  const sentences: TemplateResult[] = [];
  sentences.push(html`We mapped <b>${fp.total} places ${state.brand} appears online</b> — ${fp.controlled} of them are surfaces the client directly controls, ${fp.claimable} could be claimed but haven't been.`);
  // rows.length > 0 iff best/worst non-null (see derive/search.ts) — same runs render
  if (sh && sh.best && sh.worst){
    sentences.push(html`On Google, ownership of the branded page-1 results is <b>strongest in ${listJoin(sh.bestLocs)} (${sh.best.owned} of ${sh.best.slots}) and weakest in ${listJoin(sh.worstLocs)} (${sh.worst.owned} of ${sh.worst.slots})</b>${sh.negLocs.length ? html`, with an Undesirable result on page 1 in ${listJoin(sh.negLocs)}` : nothing}.`);
  }
  if (ah){
    // HONESTY (coverage-honesty audit P2-1): don't state a "0% owned" figure
    // when source ownership was never measured (aiHeadline preserves null).
    // Say it plainly instead — mirroring the diff surface's "not comparable".
    const ownMeasured = ah.ownership != null;
    const ownLead = ownMeasured
      ? html`only <b>${pct(ah.ownership)}% of what they cite comes from sources the client controls</b>`
      : html`<b>source ownership of what they cite wasn't measured this run</b>`;
    sentences.push(html`When AI engines answer questions about ${state.brand}, ${ownLead}${pr ? html` — ${ownMeasured ? 'and' : 'though'} counting own-domain citations alone, ${state.brand} ranks ${pr.rank === pr.of ? 'last' : `#${pr.rank}`} in its ${pr.of}-brand tracked set (${pct(pr.client.selfCitationShare)}% vs ${pr.best.name}'s ${pct(pr.best.selfCitationShare)}%)` : nothing}${ah.unanimousNeg ? `, while all ${ah.engines} engines repeat ${ah.unanimousNeg} negative theme${ah.unanimousNeg>1?'s':''} about the brand` : ''}.`);
  }
  if (eh){
    const kpPhrase = !eh.kpPresent ? html`is <b>missing</b>`
      : eh.kpClaimed === 'yes' ? html`exists and is <b>claimed</b>`
      : eh.kpClaimed === 'unknown' ? html`exists but its <b>claim status could not be verified</b>`
      : html`exists but is <b>not claimed</b>`;
    sentences.push(html`The Google ${tipT('Knowledge Panel','Knowledge Panel')} ${kpPhrase}, and ${eh.fix} of the ${eh.total} ${tipT('Wikidata','Wikidata')} facts behind it need fixing or adding${eh.measured && eh.bridges ? html`; the link crawl found <b>${eh.bridges} missing cross-links</b> between the client's own properties${eh.orphans ? ` and ${eh.orphans} orphaned properties nothing points at` : ''}` : eh.weak ? `; ${eh.weak} official profiles have no authoritative link proving they belong to the client` : ''}.`);
  }
  if (state.conflicts.length){
    sentences.push(html`${state.conflicts.length} factual conflicts run across the footprint — starting with <b>which website is the official one</b> — and each has a receipt in §6.`);
  }

  render(html`
    ${synthesis ? html`
    <section class="syn-program" aria-label="program synthesis">
      <div class="syn-score card">
        <div class="syn-score-top">
          <div>
            <div class="syn-kicker">Baseline score</div>
            <div class="syn-number">${synthesis.baselineScore.score}<small>/${synthesis.baselineScore.denominator}</small></div>
          </div>
          <div class="syn-provisional">${synthesis.baselineScore.provisionalLabel}</div>
        </div>
        <div class="syn-track" role="meter" aria-valuemin="0" aria-valuemax="${synthesis.baselineScore.denominator}"
          aria-valuenow="${synthesis.baselineScore.score}" aria-label="Baseline Score v0">
          <span style="width:${synthesis.baselineScore.denominator ? Math.round(100 * synthesis.baselineScore.score / synthesis.baselineScore.denominator) : 0}%"></span>
        </div>
        <div class="syn-components">
          ${synthesis.baselineScore.components.map((component) => html`
            <span class="syn-component ${component.measured ? '' : 'unmeasured'}" title="${component.inputSummary}">
              ${component.label} <b>${component.measured ? `${component.earned}/${component.max}` : 'not measured'}</b>
            </span>`)}
        </div>
        <div class="syn-measured">measured over ${synthesis.baselineScore.measuredComponents} of ${synthesis.baselineScore.totalComponents} components</div>
      </div>

      <div class="syn-narrative">
        ${synthesis.executiveNarrative.map((clause) => html`<p title="provenance: ${clause.provenance}">${clause.text}<sup>↗</sup></p>`)}
      </div>

      <div class="syn-findings">
        ${synthesis.rankedFindings.slice(0, 5).map((finding, index) => {
          const task = wsAvailable() ? WS.tasks.find((t) => finding.taskKeys.includes(t.action_key || '')) : null;
          const href = task ? buildTaskHref({ clientSlug: WS.clientSlug || '', taskId: task.id, runFile: state.runFile }) : '';
          return html`<article class="syn-finding card">
            <div class="syn-rank">${index + 1}</div>
            <div class="syn-finding-body">
              <h3>${finding.title}</h3>
              <p><b>Why it matters</b> — ${finding.whyItMatters}</p>
              <p><b>Recommended action</b> — ${finding.recommendedAction}</p>
              <div class="syn-task">
                ${task ? html`<a href="${href}">${task.title}</a><span class="wsst s-${task.status} pill-sm">${WS.STATUS_LABEL[task.status] || task.status}</span>`
                  : html`<span>no task derived — needs AM judgment</span>`}
              </div>
              <div class="syn-source">${finding.provenance}</div>
            </div>
            <span class="syn-stat" title="source: ${finding.stat.sourcePath}"><b>${finding.stat.value}</b>${finding.stat.label}</span>
          </article>`;
        })}
      </div>
    </section>` : nothing}

    <div class="identity">
      ${state.brandLogo ? html`<img class="logo wide" src="${state.brandLogo}" alt="">` : core ? html`<img class="logo" src="${faviconUrl(core.url, 128)}" alt="">` : html`<div class="logo"></div>`}
      <div class="idmain">
        <h2 style="font-size:26px">${state.brand}</h2>
        <div style="font-size:14px;color:var(--muted);margin-top:3px">${(ent && ent.descriptor) || 'Digital presence profile'}</div>
        ${core ? html`<a href="${core.url}" target="_blank" rel="noopener" style="display:inline-flex;align-items:center;gap:7px;margin-top:10px;font-size:12px;font-weight:600;color:var(--f1);background:var(--azure-wash);border-radius:var(--r-pill);padding:5px 13px">◆ ${domainOf(core.url) || core.title}</a>` : nothing}
      </div>
      ${factRows.length ? html`<dl class="idfacts">${factRows}</dl>` : nothing}
    </div>

    ${cov ? html`
    <div class="card covpanel">
      <div class="covhead">
        <div class="covtitle">
          <h3>Coverage confidence</h3>
          <div class="sub">every research layer with its search &amp; fetch counts — thin layers flagged, not buried in the appendix</div>
        </div>
        <span class="pill-state ${cov.clean ? 'ok' : 'warn'} covchip"
          title="${cov.checkedCount} of ${cov.inScopeTotal} in-scope research layers were actually covered${cov.thinCount ? `; ${cov.thinCount} flagged thin` : ''}${cov.scopedCount ? `; ${cov.scopedCount} deliberately scoped out by the client profile` : ''}">
          ${covChip}
        </span>
      </div>
      <div class="covgrid">
        ${cov.layers.map((l) => html`
          <div class="covlayer ${l.scoped ? 'scoped' : l.checked ? (l.thin ? 'thin' : 'ok') : 'gap'}"
            title="${l.scoped ? 'deliberately scoped out by the client profile — not checked, not missing' : l.thinNote || (l.checked ? '' : 'this layer was not run in this scope')}">
            <div class="cl-top"><span class="cl-n">${l.n}</span><span class="cl-name">${l.label}</span></div>
            <div class="cl-meta">
              ${l.scoped
                ? html`<span class="cl-flag scoped">scoped out</span>`
                : html`${l.source === 'mapping'
                    ? covCountText(l)
                    : html`<span class="pill-state acc pill-sm">${l.source}</span>${l.measuredNote}`}${l.thin ? html`<span class="cl-flag">${l.checked ? 'thin' : 'not run'}</span>` : nothing}`}
            </div>
          </div>`)}
      </div>
      <div class="caption">${state.impact || state.aiNarrative ? html`Search &amp; AI layers are measured downstream by IMPACT and AIQ — reconciled here, so these are the final counts used across the page. ` : nothing}<a href="#s8" style="color:var(--azure);font-weight:600">Full per-phase notes →</a></div>
    </div>` : nothing}

    <div class="cardrow" style="grid-template-columns:2fr 1fr">
      <div class="card">
        <h3>Official channels</h3>
        <div class="sub">the properties and flagship profiles the client runs — dot = health</div>
        <div class="chan" id="execChan">${chanCards}</div>
        <div class="chartwrap" style="margin-top:16px;border-top:1px solid var(--line);padding-top:12px">
          ${ch3.total ? stackedBarSvg(ch3, ch3aria) : nothing}
          <div class="ledgerkey" style="margin-top:10px">${ledgerLegend}</div>
          ${chartCaption(ch3cap)}
          ${ch3.total ? chartExportBar({ title: 'Ownership mix', caption: ch3cap, tableCols: ch3cols, tableRows: ch3rows }) : nothing}
          ${chartTableTwin(ch3cols, ch3rows)}
        </div>
        <div class="caption" style="margin-top:8px">${CONTROL_TIER_LINE_T}</div>
      </div>
      <div class="card">
        <h3>Noticeably missing or unclaimed</h3>
        <div class="sub">gaps a searcher or AI engine fills with someone else's content</div>
        <div class="gapchips">${gaps.length ? gaps : html`<span style="font-size:12.5px;color:var(--muted)">No gaps recorded this run.</span>`}</div>
        <div class="caption"><a href="#s6" style="color:var(--azure);font-weight:600">All gaps with rationale →</a></div>
      </div>
    </div>

    <div class="card" style="margin-bottom:16px">
      <h3>What we found</h3>
      <div class="sub">the audit in ${sentences.length} sentences — each one is unpacked, with its evidence, in the section it links to</div>
      <ul class="findlist">${sentences.map(s => html`<li>${s}</li>`)}</ul>
      <div class="caption">A live digital-presence hub, not a static report: built from measured data (IMPACT search polling, AIQ engine citations, the link crawl, and a full asset-mapping research run), refreshed on demand, with a tracked action backlog in §7.</div>
    </div>

    <div class="verdicts">${verdicts.map(v => html`
      <div class="vcard ${v.st}">
        <div class="vt">${v.plane}</div>
        <div class="vnum">${numT(v.num)}</div>
        <div style="font-size:11px;color:var(--muted);margin-bottom:6px">${v.numLabel}</div>
        <span class="vstate">${v.st === 'win' ? 'In good shape' : v.st === 'hold' ? 'Needs work' : 'Exposed'}</span>
        <div class="vr">${v.read} <a href="${v.anchor}" style="color:var(--azure);font-weight:600">Details →</a></div>
      </div>`)}</div>

    ${(() => {
      // one master list: when the shared board is live, the top-five reads
      // from it (same rows, same statuses as §7) and statuses can be changed
      // right here; otherwise the derived preview renders read-only.
      const wsOn = wsAvailable();
      if (wsOn){
        const top = WS.topMoves(5);
        return html`<div class="card">
      <h3>Where to start — the top five moves</h3>
      <div class="sub">the highest-priority actionable items from the shared board — same list, same live statuses as §7</div>
      <div class="top5">${top.map((t) => html`
        <div class="actrow"><div class="aw"><b>${t.title}</b> — ${(t.detail||'').length > 150 ? (t.detail||'').slice(0,147)+'…' : (t.detail||'')}</div>
          <span class="prio ${t.priority}">${cap(t.priority)}</span>
          <button class="wsst s-${t.status}" data-wsact="${t.id}" title="${WS.STATUS_FULL[t.status]||t.status} — click to change" @click=${(ev: MouseEvent) => wsactClick(ev, t.id)}>${WS.STATUS_LABEL[t.status] || t.status}</button></div>`)}</div>
      <div class="caption"><a href="#s7" style="color:var(--azure);font-weight:600">See the full action plan →</a></div>
    </div>`;
      }
      return html`<div class="card">
      <h3>Where to start — the top five moves</h3>
      <div class="sub">the first five of ${acts.length} recommendations in strict priority order — the full ranked table is §7</div>
      <div class="top5">${acts.slice(0,5).map((a) => html`
        <div class="actrow"><div class="aw"><b>${a.what}</b> — ${a.detail.length > 160 ? a.detail.slice(0,157)+'…' : a.detail}</div><span class="prio ${a.priority}">${cap(a.priority)}</span></div>`)}</div>
      <div class="caption"><a href="#s7" style="color:var(--azure);font-weight:600">See the full action plan →</a></div>
    </div>`;
    })()}`, el);
}

// F4 item 3 — see the module doc comment: re-renders §0 whenever WS's data
// changes, replacing the direct renderExec(state) call state/workspace.ts's
// repaintAll()/init() used to make.
wsChangeStore.subscribe(() => renderExec(payloadStore.get()));
